// src/components/USLocationDialog.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Chip,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { COUNTRY_POSTAL } from "../countryPostal";
import api from "../services/api";

export default function USLocationDialog({
  open,
  country = "US", // default για ασφάλεια
  onCancel,
  onConfirm,
  lockOpen = false,
}) {
  const { t } = useTranslation();

  const [statesData, setStatesData] = useState([]); // [{ state, cities: [{city, zips:[]}, ...] }, ...]
  const [loading, setLoading] = useState(false);
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [zip, setZip] = useState("");

  // labels/παραδείγματα από COUNTRY_POSTAL
  const { postal_label, postal_example } =
    COUNTRY_POSTAL[country] || COUNTRY_POSTAL.US;

  // 🔹 Φόρτωμα από backend όταν ανοίγει το dialog
  useEffect(() => {
    if (!open) return;

    setLoading(true);
    api.shoppingList
      .getUSSupportedLocations()
      .then((res) => {
        const list = res?.data?.states;
        if (Array.isArray(list) && list.length > 0) {
          setStatesData(list);

          // Προεπιλογή πολιτείας: CA αν υπάρχει, αλλιώς η πρώτη
          const hasCA = list.find((s) => s.state === "CA");
          const initialState = hasCA ? "CA" : list[0].state;
          setSelectedState(initialState);

          // Προεπιλογή πόλης: η πρώτη της πολιτείας
          const stateObj = list.find((s) => s.state === initialState);
          if (stateObj && stateObj.cities && stateObj.cities.length > 0) {
            setSelectedCity(stateObj.cities[0].city);
            // Προεπιλογή ZIP: το πρώτο
            const firstZips = stateObj.cities[0].zips || [];
            if (firstZips.length > 0) {
              setZip(firstZips[0]);
            } else {
              setZip("");
            }
          } else {
            setSelectedCity("");
            setZip("");
          }
        } else {
          setStatesData([]);
          setSelectedState("");
          setSelectedCity("");
          setZip("");
        }
      })
      .catch((err) => {
        console.error("Failed to load US supported locations:", err);
        setStatesData([]);
        setSelectedState("");
        setSelectedCity("");
        setZip("");
      })
      .finally(() => setLoading(false));
  }, [open]);

  // 🔹 State options
  const stateOptions = useMemo(
    () => statesData.map((s) => s.state),
    [statesData]
  );

  // 🔹 Cities για την επιλεγμένη πολιτεία
  const cityOptions = useMemo(() => {
    const stateObj = statesData.find((s) => s.state === selectedState);
    if (!stateObj || !Array.isArray(stateObj.cities)) return [];
    return stateObj.cities.map((c) => c.city);
  }, [statesData, selectedState]);

  // 🔹 Zips για την επιλεγμένη πόλη
  const zipsForCity = useMemo(() => {
    const stateObj = statesData.find((s) => s.state === selectedState);
    if (!stateObj || !Array.isArray(stateObj.cities)) return [];
    const cityObj = stateObj.cities.find((c) => c.city === selectedCity);
    if (!cityObj || !Array.isArray(cityObj.zips)) return [];
    return cityObj.zips;
  }, [statesData, selectedState, selectedCity]);

  // 🔹 Validation: 5 ψηφία ΚΑΙ να είναι μέσα στη λίστα zipsForCity
  const valid = useMemo(() => {
    const v = (zip || "").trim();
    if (!/^\d{5}$/.test(v)) return false;
    if (zipsForCity.length > 0) {
      return zipsForCity.includes(v);
    }
    return true;
  }, [zip, zipsForCity]);

  const handleStateChange = (e) => {
    const newState = e.target.value;
    setSelectedState(newState);
    // reset city + zip
    const stateObj = statesData.find((s) => s.state === newState);
    if (stateObj && stateObj.cities && stateObj.cities.length > 0) {
      const firstCity = stateObj.cities[0].city;
      setSelectedCity(firstCity);
      const firstZips = stateObj.cities[0].zips || [];
      setZip(firstZips[0] || "");
    } else {
      setSelectedCity("");
      setZip("");
    }
  };

  const handleCityChange = (e) => {
    const newCity = e.target.value;
    setSelectedCity(newCity);
    const stateObj = statesData.find((s) => s.state === selectedState);
    if (!stateObj) {
      setZip("");
      return;
    }
    const cityObj = stateObj.cities.find((c) => c.city === newCity);
    if (cityObj && cityObj.zips && cityObj.zips.length > 0) {
      setZip(cityObj.zips[0]);
    } else {
      setZip("");
    }
  };

  const handleConfirm = () => {
    const cleaned = (zip || "").trim();
    if (!valid) return;
    // Το onConfirm περιμένει ακόμα μόνο ZIP (όπως πριν)
    onConfirm && onConfirm(cleaned);
  };

  const visibleZips = useMemo(() => {
    if (!zipsForCity) return [];
    // για να μην χαθεί ο χρήστης, δείξε μέχρι 20
    return zipsForCity.slice(0, 20);
  }, [zipsForCity]);

  const handleDialogClose = (e, reason) => {
    if (lockOpen && (reason === "backdropClick" || reason === "escapeKeyDown")) {
      onCancel && onCancel(reason);
      return;
    }
    onCancel && onCancel(reason);
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown={lockOpen}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        {t("postal_code_label", { postal_label }) || "Βάλε ZIP Code"}
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t("zip_state_city_hint") ||
            "Επίλεξε πολιτεία, πόλη και μετά ένα υποστηριζόμενο ZIP."}
        </Typography>

        {/* Επιλογή Πολιτείας */}
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>{t("state") || "Πολιτεία"}</InputLabel>
          <Select
            label={t("state") || "Πολιτεία"}
            value={selectedState}
            onChange={handleStateChange}
          >
            {stateOptions.map((st) => (
              <MenuItem key={st} value={st}>
                {st}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Επιλογή Πόλης */}
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>{t("city") || "Πόλη"}</InputLabel>
          <Select
            label={t("city") || "Πόλη"}
            value={selectedCity}
            onChange={handleCityChange}
            disabled={!selectedState || cityOptions.length === 0}
          >
            {cityOptions.map((city) => (
              <MenuItem key={city} value={city}>
                {city}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Εισαγωγή ZIP */}
        <TextField
          fullWidth
          label={t("zip_code") || "ZIP Code"}
          placeholder={zipsForCity[0] || "90037"}
          value={zip}
          onChange={(e) => setZip(e.target.value)}
          inputProps={{ inputMode: "numeric", pattern: "\\d{5}" }}
          helperText={
            !valid && zip
              ? t("zip_must_be_5_digits_supported") ||
              t("zip_must_be_5_digits") ||
              "Βάλε ένα υποστηριζόμενο ZIP (5 ψηφία)."
              : t("postal_code_helper", {
                postal_label,
                postal_example,
              }) || " "
          }
          error={!!zip && !valid}
        />

        {/* Προτεινόμενα ZIPs για την πόλη */}
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
            {t("supported_zips_for_city") ||
              "Υποστηριζόμενα ZIPs για την επιλεγμένη πόλη"}
            {loading ? ` (${t("loading") || "φόρτωση"}...)` : ""}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {visibleZips.map((z) => (
              <Chip
                key={z}
                label={z}
                size="small"
                onClick={() => setZip(z)}
              />
            ))}
            {zipsForCity.length > visibleZips.length && (
              <Typography
                variant="caption"
                sx={{ mt: 1, display: "block", color: "text.secondary" }}
              >
                {t("and_more_zips") ||
                  `...και άλλα ${zipsForCity.length - visibleZips.length
                  } ZIPs`}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => {
            if (lockOpen) {
              onCancel && onCancel("cancel");
              return;
            }
            onCancel && onCancel("cancel");
          }}
        >
          {t("cancel") || "Άκυρο"}
        </Button>
        <Button variant="contained" disabled={!valid} onClick={handleConfirm}>
          {t("continue") || "Συνέχεια"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
